import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase, checkSupabaseConnection, getUserRecord, createUserRecord } from '../utils/supabase';
import { useToast } from './ToastContext';
import { useSettings } from './SettingsContext';
import { logError } from '../utils/errorLogging';
import { createLogger } from '../utils/logger';
import { env } from '../utils/env';
import { useBusinessMetrics } from '../hooks/useStatsigFeatures';
import { reinitializeSubscriptions } from '../services/appInitialization';
import { setUserContext, setContext, addBreadcrumb } from '../config/sentry';
import { updateStatsigUserContext } from '../utils/sentryStatsigIntegration';
import { userDataCache, EnhancedUserData } from '../utils/userDataCache';

// Add this interface declaration for the global window object
declare global {
  interface Window {
    __MFA_SIGNIN_STARTED?: boolean;
    __mfaClickListenerAdded?: boolean;
    originalPushState?: History['pushState'];
    originalReplaceState?: History['replaceState'];
  }
}

// Logger types
type LogData = Record<string, unknown>;

// Helper function for type-safe logging
const safeLog = (logger: any, level: 'debug' | 'info' | 'warn' | 'error', message: string, data?: any) => {
  try {
    if (!data) {
      logger[level](message);
      return;
    }
    
    // Ensure data is a plain object for logging
    const safeData: LogData = {};
    Object.entries(data).forEach(([key, value]) => {
      if (value instanceof Error) {
        safeData[key] = value.message;
      } else if (typeof value === 'object' && value !== null) {
        try {
          // Try to convert to string if an object with toString
          safeData[key] = JSON.stringify(value);
        } catch (e) {
          safeData[key] = '[Object]';
        }
      } else {
        safeData[key] = value;
      }
    });
    
    logger[level](message, safeData);
  } catch (e) {
    // Fallback in case of error
    console[level](`[${logger.module}] ${message}`, data);
  }
};

const logger = createLogger({ module: 'Auth', level: 'info' });

// Add auth caching
const AUTH_CACHE_DURATION = 30000; // 30 second cache (reduced from 60s)
let cachedUser: { user: User | null; timestamp: number } | null = null;
let pendingAuthPromise: Promise<{ user: User | null }> | null = null;

// Add session stability to prevent auth state flapping
const AUTH_STABILITY_DURATION = 1000; // 1 second of stable auth state
let lastAuthStateChange = Date.now();
let hasHadAuthSession = false;

// Cached auth function to reduce Supabase calls
const getCachedUser = async () => {
  const now = Date.now();
  
  // Return cached user if valid
  if (cachedUser && (now - cachedUser.timestamp < AUTH_CACHE_DURATION)) {
    return { user: cachedUser.user };
  }
  
  // If there's already a pending request, wait for it
  if (pendingAuthPromise) {
    return pendingAuthPromise;
  }
  
  // Create a new request
  pendingAuthPromise = (async () => {
    try {
      const { data, error } = await supabase.auth.getUser();
      
      if (error) {
        if (error.name !== 'AuthSessionMissingError') {
          logger.error('Error in getCachedUser:', error);
        }
        cachedUser = { user: null, timestamp: now };
        return { user: null };
      }
      
      cachedUser = { user: data.user, timestamp: now };
      return { user: data.user };
    } catch (error) {
      logger.error('Exception in getCachedUser:', error);
      cachedUser = { user: null, timestamp: now };
      return { user: null };
    } finally {
      pendingAuthPromise = null;
    }
  })();
  
  return pendingAuthPromise;
};

// Helper functions for local storage
const getLocalOnboardingStatus = (userId: string): boolean => {
  try {
    const key = `onboarding_complete_${userId}`;
    const value = localStorage.getItem(key) === 'true';
    console.log('Checking localStorage onboarding status:', { userId, key, value });
    return value;
  } catch (e) {
    console.error('Error accessing localStorage:', e);
    return false;
  }
};

const setLocalOnboardingStatus = (userId: string, status: boolean): void => {
  try {
    const key = `onboarding_complete_${userId}`;
    localStorage.setItem(key, status.toString());
  } catch (e) {
    // Ignore localStorage errors
  }
};

// MFA related types
interface MFAFactor {
  id: string;
  friendly_name?: string;
  factor_type: 'totp' | 'phone' | string;
  status: 'verified' | 'unverified';
}

// MFA Session storage keys
const MFA_PENDING_KEY = 'mfa_verification_pending';
const MFA_FACTOR_ID_KEY = 'mfa_factor_id';
const MFA_EMAIL_KEY = 'mfa_email';

// Add this interface near the top of the file with other interfaces
interface SignInResult {
  success: boolean;
  requiresMfa: boolean;
  error?: Error;
}

interface AuthContextType {
  user: User | null;
  userProfile: EnhancedUserData | null; // Complete user profile data
  loading: boolean;
  onboardingComplete: boolean;
  onboardingLoading: boolean;
  signOut: () => Promise<void>;
  updateOnboardingStatus: () => Promise<void>;
  preloadUserStatus: (email: string) => Promise<void>;
  refreshUserProfile: () => Promise<void>; // Force refresh user profile
  isAdmin: boolean; // Cached admin status
  // MFA related methods
  mfaEnabled: boolean;
  mfaLoading: boolean;
  mfaFactors: MFAFactor[];
  refreshMFAStatus: () => Promise<void>;
  unenrollMFA: (factorId: string) => Promise<void>;
  // Login with MFA flow
  isMfaPending: boolean;
  currentMfaFactorId: string | null;
  mfaEmail: string | null;
  signInWithMfa: (email: string, password: string) => Promise<SignInResult>;
  completeMfaVerification: (code: string) => Promise<{
    success: boolean;
    error?: Error;
  }>;
  cancelMfaVerification: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<EnhancedUserData | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [onboardingComplete, setOnboardingComplete] = useState(false);
  const [onboardingLoading, setOnboardingLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [preloadedEmail, setPreloadedEmail] = useState<string | null>(null);
  const [preloadedOnboardingStatus, setPreloadedOnboardingStatus] = useState<boolean | null>(null);
  const [preloadSuccess, setPreloadSuccess] = useState(false);
  
  // MFA state
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [mfaLoading, setMfaLoading] = useState(false);
  const [mfaFactors, setMfaFactors] = useState<MFAFactor[]>([]);
  
  // MFA verification state
  const [isMfaPending, setIsMfaPending] = useState(false);
  const [currentMfaFactorId, setCurrentMfaFactorId] = useState<string | null>(null);
  const [mfaEmail, setMfaEmail] = useState<string | null>(null);
  
  const { showToast } = useToast();
  const { preloadUserSettings } = useSettings();
  
  // Use business metrics - the hook itself should handle when Statsig isn't available
  const businessMetrics = useBusinessMetrics();
  
  // Add refs for debouncing and tracking mounted state
  const authStateTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const isMountedRef = useRef<boolean>(true);

  // Add a timestamp ref to track when MFA verification started
  const mfaVerificationStartedAtRef = useRef<number | null>(null);

  // Add flag to track if logout is in progress
  // Create refs instead of module-level variables to properly track state across renders
  const isLogoutInProgressRef = useRef(false);
  const logoutToastShownRef = useRef(false);

  // Initialize userDataCache subscription
  useEffect(() => {
    const unsubscribe = userDataCache.subscribeToAuthChanges();
    return unsubscribe;
  }, []);

  // Initialize MFA state from session storage
  useEffect(() => {
    try {
      const pendingMfa = sessionStorage.getItem(MFA_PENDING_KEY) === 'true';
      const factorId = sessionStorage.getItem(MFA_FACTOR_ID_KEY);
      const email = sessionStorage.getItem(MFA_EMAIL_KEY);
      
      if (pendingMfa && factorId) {
        setIsMfaPending(true);
        setCurrentMfaFactorId(factorId);
        if (email) {
          setMfaEmail(email);
        }
        
        logger.debug('Restored MFA pending state from session storage', {
          factorId,
          email
        });
      }
    } catch (e) {
      logger.warn('Error restoring MFA state from session storage', { error: e });
    }
  }, []);

  // Store MFA state in session storage when it changes
  useEffect(() => {
    try {
      if (isMfaPending && currentMfaFactorId) {
        sessionStorage.setItem(MFA_PENDING_KEY, 'true');
        sessionStorage.setItem(MFA_FACTOR_ID_KEY, currentMfaFactorId);
        if (mfaEmail) {
          sessionStorage.setItem(MFA_EMAIL_KEY, mfaEmail);
        }
      } else {
        sessionStorage.removeItem(MFA_PENDING_KEY);
        sessionStorage.removeItem(MFA_FACTOR_ID_KEY);
        sessionStorage.removeItem(MFA_EMAIL_KEY);
      }
    } catch (e) {
      logger.warn('Error storing MFA state in session storage', { error: e });
    }
  }, [isMfaPending, currentMfaFactorId, mfaEmail]);

  // Fetch MFA status for the current user
  const refreshMFAStatus = useCallback(async () => {
    // Skip if there's no user, immediately clear MFA state
    if (!user) {
      setMfaEnabled(false);
      setMfaFactors([]);
      return;
    }
    
    // Check if we have an active session first
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        logger.debug('No active session when refreshing MFA status - skipping');
        setMfaEnabled(false);
        setMfaFactors([]);
        return;
      }
    } catch (sessionError) {
      // If we can't get the session, we're definitely not authenticated
      logger.debug('Error checking session during MFA refresh - skipping', { error: sessionError });
      setMfaEnabled(false);
      setMfaFactors([]);
      return;
    }
    
    setMfaLoading(true);
    
    try {
      // Get all enrolled factors with retry logic
      let data = null;
      let error = null;
      let retryCount = 0;
      const maxRetries = 2;
      
      while (retryCount < maxRetries) {
        try {
          // Add small delay on retries
          if (retryCount > 0) {
            await new Promise(resolve => setTimeout(resolve, 300 * retryCount));
          }
          
          const result = await supabase.auth.mfa.listFactors();
          data = result.data;
          error = result.error;
          
          if (!error) {
            break;
          }
          
          logger.debug(`MFA listFactors in refreshMFAStatus attempt ${retryCount + 1} failed:`, error);
          retryCount++;
        } catch (e) {
          logger.debug(`MFA listFactors in refreshMFAStatus attempt ${retryCount + 1} threw error:`, e);
          error = e;
          retryCount++;
        }
      }
      
      if (error) throw error;
      
      // Get verified MFA factors
      const verifiedFactors = data.totp.filter(factor => factor.status === 'verified');
      
      setMfaEnabled(verifiedFactors.length > 0);
      setMfaFactors(data.totp);
      
      logger.debug('MFA status refreshed:', {
        enabled: verifiedFactors.length > 0,
        factorCount: data.totp.length,
        verifiedCount: verifiedFactors.length
      });
    } catch (error) {
      // Check specifically for auth session errors
      const isAuthError = 
        error && 
        typeof error === 'object' && 
        (
          ('name' in error && error.name === 'AuthSessionMissingError') ||
          ('message' in error && 
            typeof error.message === 'string' && 
            (error.message.includes('Auth session missing') || 
             error.message.includes('JWT expired')))
        );
      
      if (isAuthError) {
        // This is expected during logout, handle gracefully
        logger.debug('Auth session missing when refreshing MFA - user likely logged out');
        setMfaEnabled(false);
        setMfaFactors([]);
      } else {
        // For other errors, log them properly
        logger.error('Error fetching MFA status:', error);
        await logError(error, 'AuthContext.refreshMFAStatus', { userId: user.id });
      }
    } finally {
      setMfaLoading(false);
    }
  }, [user]);

  // Unenroll from MFA
  const unenrollMFA = async (factorId: string) => {
    if (!user) return;
    
    try {
      const { error } = await supabase.auth.mfa.unenroll({ factorId });
      
      if (error) throw error;
      
      await refreshMFAStatus();
      showToast('Two-factor authentication disabled successfully', 'success');
    } catch (error) {
      logger.error('Error unenrolling from MFA:', error);
      await logError(error, 'AuthContext.unenrollMFA', { userId: user.id, factorId });
      showToast('Failed to disable two-factor authentication', 'error');
      throw error;
    }
  };

  // Helper function to clear all MFA-related flags at once
  const clearAllMfaFlags = () => {
    try {
      logger.debug('Clearing MFA flags');
      
      // Clear React state
      setIsMfaPending(false);
      setCurrentMfaFactorId(null);
      setMfaEmail(null);
      
      // Clear session storage flags
      sessionStorage.removeItem(MFA_PENDING_KEY);
      sessionStorage.removeItem(MFA_FACTOR_ID_KEY);
      sessionStorage.removeItem(MFA_EMAIL_KEY);
      sessionStorage.removeItem('mfa_signin_in_progress');
      sessionStorage.removeItem('mfa_verification_completed');
      sessionStorage.removeItem('mfa_ultra_protection');
      sessionStorage.removeItem('mfa_flags_set_time');
      sessionStorage.removeItem('mfa_verification_start_time');
      
      // Clear global JavaScript flags
      if (typeof window !== 'undefined') {
        window.__MFA_SIGNIN_STARTED = false;
        window.__mfaClickListenerAdded = false;
      }
      
      logger.debug('Successfully cleared MFA flags');
    } catch (e) {
      logger.error('Error in clearAllMfaFlags:', e);
    }
  };

  // Debounced state update function
  const updateAuthState = useCallback((
    newUser: User | null,
    newOnboardingComplete: boolean,
    newLoading: boolean,
    newOnboardingLoading: boolean
  ) => {
    if (!isMountedRef.current) return;

    if (authStateTimeoutRef.current) {
      clearTimeout(authStateTimeoutRef.current);
    }

    // Implement stability check to prevent rapid auth state changes
    const now = Date.now();
    const timeSinceLastChange = now - lastAuthStateChange;
    
    // If we had a user and now trying to set to null, enforce stability period
    if (user && !newUser && timeSinceLastChange < AUTH_STABILITY_DURATION && hasHadAuthSession) {
      logger.debug('🛡️ Ignoring rapid auth state change (user → null) during stability period', {
        timeSinceLastChange,
        stabilityDuration: AUTH_STABILITY_DURATION
      });
      return;
    }
    
    // Update last auth state change timestamp when changing auth state
    if ((user && !newUser) || (!user && newUser)) {
      lastAuthStateChange = now;
      
      // Track that we've had an auth session
      if (newUser) {
        hasHadAuthSession = true;
        // Store in session storage as backup
        try {
          sessionStorage.setItem('has_had_auth_session', 'true');
        } catch (e) {
          // Ignore storage errors
        }
      }
    }

    authStateTimeoutRef.current = setTimeout(() => {
      if (!isMountedRef.current) return;
      
      setUser(newUser);
      setOnboardingComplete(newOnboardingComplete);
      setLoading(newLoading);
      setOnboardingLoading(newOnboardingLoading);
      
      // Fetch complete user profile when user changes
      if (newUser && newUser.id) {
        userDataCache.getUserData(newUser.id).then(profile => {
          if (profile && isMountedRef.current) {
            setUserProfile(profile);
            setIsAdmin(profile.user_type === 'admin');
            // Update onboarding status from profile if available
            if (typeof profile.onboarding_complete === 'boolean') {
              setOnboardingComplete(profile.onboarding_complete);
            }
          }
        });
      } else {
        setUserProfile(null);
        setIsAdmin(false);
      }
      
      // Update Sentry user context
      if (newUser) {
        setUserContext({
          id: newUser.id,
          email: newUser.email,
          username: newUser.user_metadata?.username,
        });
        
        // Set additional user context
        setContext('user_details', {
          subscription_tier: newUser.user_metadata?.subscription_tier,
          created_at: newUser.created_at,
          last_sign_in: newUser.last_sign_in_at,
          onboarding_complete: newOnboardingComplete,
          email_confirmed: newUser.email_confirmed_at ? true : false,
        });
        
        // Update Statsig context in Sentry
        updateStatsigUserContext(newUser.id);
        
        // Add breadcrumb for sign in
        addBreadcrumb('User signed in', 'auth', {
          user_id: newUser.id,
          email: newUser.email,
        }, 'info');
      } else {
        setUserContext(null);
        updateStatsigUserContext(null);
        
        // Add breadcrumb for sign out
        addBreadcrumb('User signed out', 'auth', {}, 'info');
      }
      
      // Refresh MFA status when user changes
      if (newUser) {
        refreshMFAStatus();
      } else {
        setMfaEnabled(false);
        setMfaFactors([]);
      }
    }, 50); // 50ms debounce
  }, [refreshMFAStatus, user]);

  // Updated function to use userDataCache
  const checkOnboardingStatusFromDB = async (userId: string): Promise<boolean | null> => {
    try {
      // First check if we're actually authenticated
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        logger.debug('No active session when checking onboarding status - skipping DB query');
        return null;
      }
      
      // Use userDataCache for deduplication
      const isComplete = await userDataCache.getOnboardingStatus(userId);
      
      // Update localStorage for future quicker access - but always trust DB first
      if (isComplete) {
        setLocalOnboardingStatus(userId, true);
      } else {
        // Clear localStorage to prevent stale data
        try {
          localStorage.removeItem(`onboarding_complete_${userId}`);
        } catch (e) {
          // Ignore localStorage errors
        }
      }
      
      return isComplete;
    } catch (e) {
      logger.error('Exception checking onboarding status from DB:', e);
      return null;
    }
  };

  // Function to refresh user profile data
  const refreshUserProfile = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      const profile = await userDataCache.getUserData(user.id, { fresh: true });
      if (profile) {
        setUserProfile(profile);
        setIsAdmin(profile.user_type === 'admin');
        setOnboardingComplete(profile.onboarding_complete === true);
        
        logger.debug('User profile refreshed', {
          userId: user.id,
          isAdmin: profile.user_type === 'admin',
          onboardingComplete: profile.onboarding_complete
        });
      }
    } catch (error) {
      logger.error('Error refreshing user profile:', error);
    }
  }, [user?.id]);

  // Update checkUser to use the cached function
  const checkUser = useCallback(async (showToasts = false) => {
    logger.debug('🔍 Checking user authentication and onboarding status...', {
      showToasts,
      initialized,
      preloadedEmail,
      hasPreloadedStatus: preloadedOnboardingStatus !== null
    });

    try {
      const isConnected = await checkSupabaseConnection();
      if (!isConnected) {
        throw new Error('Unable to connect to the database');
      }

      const { user: currentUser } = await getCachedUser();

      if (currentUser) {
        if (preloadedEmail === currentUser.email && 
            preloadedOnboardingStatus !== null && 
            preloadSuccess) {
          // Debug preloaded onboarding status
          logger.debug('Using preloaded onboarding status:', {
            email: preloadedEmail,
            onboardingComplete: preloadedOnboardingStatus
          });
          updateAuthState(currentUser, preloadedOnboardingStatus, false, false);
        } else {
          // Always prioritize checking the database directly for onboarding status
          const dbOnboardingStatus = await checkOnboardingStatusFromDB(currentUser.id);
          
          if (dbOnboardingStatus !== null) {
            // We have a valid DB response - use it
            if (import.meta.env.DEV) {
              logger.debug('Using database onboarding status (direct check)', {
                userId: currentUser.id,
                isComplete: dbOnboardingStatus
              });
            }
            updateAuthState(currentUser, dbOnboardingStatus, false, false);
          } else {
            // Fallback to existing getUserRecord flow
            const { data: userData, error: userDataError } = await getUserRecord(currentUser.id);
            
            if (userDataError || !userData) {
              // Error getting user data - fallback to localStorage with warning
              console.warn('Error getting user data, falling back to localStorage');
              const { error: createError } = await createUserRecord(
                currentUser.id,
                currentUser.email || '',
                false
              );

              if (createError) {
                const localOnboardingStatus = getLocalOnboardingStatus(currentUser.id);
                console.log('Using localStorage onboarding status after error:', {
                  userId: currentUser.id,
                  localOnboardingStatus
                });
                updateAuthState(currentUser, localOnboardingStatus, false, false);
              } else {
                console.log('Setting default onboarding status (false)');
                updateAuthState(currentUser, false, false, false);
              }
            } else {
              const isComplete = userData.onboarding_complete === true;
              if (import.meta.env.DEV) {
                logger.debug('Using database onboarding status (getUserRecord)', {
                  userId: currentUser.id,
                  databaseStatus: isComplete
                });
              }
              if (isComplete) {
                setLocalOnboardingStatus(currentUser.id, true);
              }
              updateAuthState(currentUser, isComplete, false, false);
            }
          }
        }
      } else {
        updateAuthState(null, false, false, false);
      }
    } catch (error) {
      logger.error('❌ Auth error:', error);
      await logError(error, 'AuthContext.checkUser', { 
        stage: 'general auth check',
        showToasts,
        initialized 
      });
      if (showToasts && initialized) {
        showToast('Authentication error occurred', 'error');
      }
      updateAuthState(null, false, false, false);
    } finally {
      setInitialized(true);
    }
  }, [initialized, showToast, preloadedEmail, preloadedOnboardingStatus, updateAuthState]);

  // Add a function to preload user onboarding status by email
  const preloadUserStatus = async (email: string) => {
    if (!email) return;
    
    logger.debug('=== Starting preloadUserStatus ===', { email });
    
    // PERFORMANCE FIX: Make this function completely non-blocking
    // Don't await any operations - let them run in background
    const preloadAsync = async () => {
      try {
        // Store the email we're preloading for
        setPreloadedEmail(email);
        setPreloadSuccess(false);

        // PERFORMANCE: Check cache first before any API calls
        const cacheKey = `beta_access_check_${email.toLowerCase()}`;
        const cachedResult = sessionStorage.getItem(cacheKey);
        
        if (cachedResult) {
          try {
            const { authorized, userData, mfaStatus, timestamp } = JSON.parse(cachedResult);
            // Use cache if less than 10 minutes old (increased from 5 minutes)
            if (Date.now() - timestamp < 10 * 60 * 1000) {
              logger.debug('Using cached beta access result from session storage');
              
              // Store MFA status in session storage for quick access during login
              if (mfaStatus) {
                try {
                  logger.debug('Using cached MFA status:', { 
                    hasMfa: mfaStatus.hasMfa,
                    factorCount: mfaStatus.factorCount || 0
                  });
                  sessionStorage.setItem('preloaded_mfa_status', JSON.stringify(mfaStatus));
                } catch (e) {
                  logger.warn('Error saving preloaded MFA status:', e);
                }
              }
              
              if (authorized && userData) {
                logger.debug('Setting preloaded status from cache:', { 
                  onboardingComplete: userData.onboarding_complete,
                  hasSettings: !!userData.settings,
                  hasUnifiedSettings: !!userData.unified_home_settings
                });
                
                setPreloadedOnboardingStatus(userData.onboarding_complete === true);
                
                // Preload settings into context/storage (non-blocking)
                if (userData.settings || userData.unified_home_settings) {
                  preloadUserSettings(email, {
                    settings: userData.settings,
                    unified_home_settings: userData.unified_home_settings
                  }).catch(error => {
                    logger.warn('Background settings preload failed:', error);
                  });
                }
                
                setPreloadSuccess(true);
                return;
              } else if (!authorized) {
                // Not authorized according to cache
                logger.debug('User not authorized for beta access (from cache)');
                setPreloadedOnboardingStatus(false);
                setPreloadSuccess(false);
                return;
              }
              // If cache doesn't have complete data, fall through to API call
            }
          } catch (error) {
            // Ignore JSON parse errors, continue with API call
            logger.warn('Invalid beta access cache format:', { error });
          }
        }

        // PERFORMANCE: Check for existing valid beta token first
        const { validateBetaToken } = await import('../utils/betaAccess');
        const betaToken = localStorage.getItem('beta_access_token');
        
        if (betaToken) {
          try {
            const hasValidToken = await validateBetaToken(betaToken);
            if (hasValidToken) {
              logger.debug('Using existing valid beta token, skipping API call');
              setPreloadedOnboardingStatus(true);
              setPreloadSuccess(true);
              return;
            }
          } catch (error) {
            logger.warn('Error validating existing beta token:', error);
            // Continue with API call
          }
        }
        
        // PERFORMANCE: Add timeout to API call to prevent hanging
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
        
        try {
          // Check beta access which now includes user data and MFA status
          const response = await fetch('/api/check-beta-access', {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json',
              'X-API-Key': env.VITE_BETA_API_KEY || ''
            },
            body: JSON.stringify({ email }),
            signal: controller.signal
          });

          clearTimeout(timeoutId);

          if (!response.ok) {
            throw new Error(`Beta access check failed: ${response.status}`);
          }

          const data = await response.json();
          
          // Store MFA status in session storage for quick access during login
          if (data.mfaStatus) {
            try {
              logger.debug('Caching MFA status for user:', { 
                hasMfa: data.mfaStatus.hasMfa,
                factorCount: data.mfaStatus.factorCount || 0
              });
              sessionStorage.setItem('preloaded_mfa_status', JSON.stringify(data.mfaStatus));
            } catch (e) {
              logger.warn('Error saving preloaded MFA status:', e);
            }
          }
          
          // Cache the result in sessionStorage to avoid redundant API calls
          sessionStorage.setItem(cacheKey, JSON.stringify({
            authorized: data.authorized,
            userData: data.userData || null,
            mfaStatus: data.mfaStatus || null,
            timestamp: Date.now()
          }));
          
          if (data.authorized && data.userData) {
            logger.debug('Setting preloaded status and settings:', { 
              onboardingComplete: data.userData.onboarding_complete,
              hasSettings: !!data.userData.settings,
              hasUnifiedSettings: !!data.userData.unified_home_settings,
              hasMfa: data.mfaStatus?.hasMfa || false
            });

            // Set onboarding status
            setPreloadedOnboardingStatus(data.userData.onboarding_complete === true);
            
            // Preload settings into context/storage (non-blocking)
            if (data.userData.settings || data.userData.unified_home_settings) {
              preloadUserSettings(email, {
                settings: data.userData.settings,
                unified_home_settings: data.userData.unified_home_settings
              }).catch(error => {
                logger.warn('Background settings preload failed:', error);
              });
            }

            setPreloadSuccess(true);
          } else {
            logger.debug('User not authorized for beta access');
            setPreloadedOnboardingStatus(false);
            setPreloadSuccess(false);
          }
        } catch (error) {
          clearTimeout(timeoutId);
          
          if (error instanceof Error && error.name === 'AbortError') {
            logger.warn('Beta access check timed out - continuing without preload');
          } else {
            logger.error('Error in preloadUserStatus:', error);
          }
          
          setPreloadedOnboardingStatus(null);
          setPreloadSuccess(false);
        }
      } catch (error) {
        logger.error('Error in preloadUserStatus async function:', error);
        setPreloadedOnboardingStatus(null);
        setPreloadSuccess(false);
      }
    };

    // PERFORMANCE FIX: Don't await - let it run in background
    preloadAsync().catch(error => {
      logger.warn('Background preload failed:', error);
    });

    logger.debug('=== preloadUserStatus started in background ===', { 
      email,
      preloadedEmail,
      preloadedOnboardingStatus,
      preloadSuccess 
    });
  };

  const updateOnboardingStatus = async () => {
    if (!user) return;

    try {
      // First update database
      const { error } = await supabase
        .from('users')
        .update({ onboarding_complete: true })
        .eq('id', user.id);

      if (error) {
        console.error('Error updating onboarding status in database:', error);
        await logError(error, 'AuthContext.updateOnboardingStatus', { userId: user.id });
        throw error;
      }

      // Update state in React context
      setOnboardingComplete(true);
      
      // Log actual database value for debugging
      console.log('updateOnboardingStatus - Set database value:', true);
      
      // Also update localStorage as a backup
      try {
        localStorage.setItem(`onboarding_complete_${user.id}`, 'true');
        console.log('updateOnboardingStatus - Set localStorage value:', true);
      } catch (e) {
        // Ignore localStorage errors
        console.error('Error setting localStorage value:', e);
      }
      
      // Verify the update was successful
      try {
        const { data: verifyData, error: verifyError } = await supabase
          .from('users')
          .select('onboarding_complete')
          .eq('id', user.id)
          .single();
        
        if (!verifyError && verifyData) {
          console.log('Verification - Database value after update:', verifyData.onboarding_complete);
        }
      } catch (e) {
        console.error('Error verifying database update:', e);
      }
      
      showToast('Onboarding completed successfully', 'success');
    } catch (error) {
      logger.error('Error updating onboarding status:', error);
      await logError(error, 'AuthContext.updateOnboardingStatus', { userId: user.id });
      showToast('Error completing onboarding', 'error');
    }
  };

  const signOut = async () => {
    try {
      // If already in progress, don't duplicate the logout process
      if (isLogoutInProgressRef.current) {
        logger.debug('Ignoring duplicate signOut call - logout already in progress');
        return;
      }
      
      isLogoutInProgressRef.current = true;
      
      if (user) {
        logger.debug('User signing out', { userId: user.id });
        
        // Track the sign out journey
        const journeyId = `user_sign_out_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        businessMetrics?.trackUserJourney?.('journey_started', {
          journey_id: journeyId,
          feature_name: 'user_sign_out'
        });
        
        // Make sure any pending telemetry data is flushed before clearing user ID
        try {
          // Give telemetry service time to complete the journey start
          await new Promise(resolve => setTimeout(resolve, 100));
          
          // Try to complete the journey synchronously before sign out
          businessMetrics?.trackUserJourney?.('journey_completed', {
            journey_id: journeyId,
            feature_name: 'user_sign_out',
            outcome: 'success',
            step_type: 'success'
          });
        } catch (telemetryError) {
          logger.debug('Non-critical error completing sign out journey:', telemetryError as Error);
        }
        
        // Clear the user from React state immediately
        updateAuthState(null, false, false, false);
        
        // Clear any stored authentication data
        try {
          // Clear auth-related data from localStorage and sessionStorage
          localStorage.removeItem('has_had_auth_session');
          sessionStorage.removeItem('has_had_auth_session');
          localStorage.removeItem('beta_access_token');
          
          // Clear settings-related cache
          localStorage.removeItem('careercard_dark_mode');
          
          // Clear any MFA-related storage
          clearAllMfaFlags();
          
          // Clear all sessionStorage
          sessionStorage.clear();
          
          // Clear all localStorage cached API responses
          Object.keys(localStorage).forEach(key => {
            if (key.startsWith('beta_access_check_') || 
                key.startsWith('user_settings_') ||
                key.startsWith('onboarding_complete_')) {
              localStorage.removeItem(key);
            }
          });
          
          // Reset our state tracking variables
          hasHadAuthSession = false;
        } catch (error) {
          logger.warn('Error clearing local storage during sign out:', { error });
        }
        
        // Show toast before the redirect to ensure it appears
        if (!logoutToastShownRef.current) {
          logoutToastShownRef.current = true;
          showToast('Successfully signed out', 'success');
        }
        
        // Perform sign out with Supabase
        try {
          const { error } = await supabase.auth.signOut({ scope: 'global' });
          if (error) {
            throw error;
          }
          logger.debug('Supabase signOut completed successfully');
        } catch (signOutError) {
          logger.error('Error during supabase.auth.signOut:', signOutError);
          // Continue with redirect even if signOut fails
        }
        
        // Force a complete page refresh and redirect to landing page
        // Add timestamp to prevent caching
        const redirectURL = `/?nocache=${Date.now()}`;
        
        // We use setTimeout to ensure the toast is visible and session is cleared
        setTimeout(() => {
          try {
            // Force a complete page reload to clear any cached state
            window.location.replace(redirectURL);
          } catch (e) {
            // Fallback to standard redirect
            window.location.href = redirectURL;
          }
        }, 500);
      } else {
        logger.debug('Sign out called with no user');
        
        // Clear all state and storage even if no user
        clearAllMfaFlags();
        sessionStorage.clear();
        localStorage.removeItem('beta_access_token');
        
        try {
          const { error } = await supabase.auth.signOut({ scope: 'global' });
          if (error) {
            throw error;
          }
        } catch (error) {
          logger.error('Error during supabase.auth.signOut with no user:', error);
        }
        
        // Add timestamp parameter to prevent caching
        window.location.replace(`/?nocache=${Date.now()}`);
      }
    } catch (error) {
      await logError(error, 'AuthContext.signOut', { userId: user?.id });
      
      // Try to redirect to landing even if there was an error
      // Add timestamp to prevent caching
      window.location.replace(`/?nocache=${Date.now()}`);
      
      // Reset logout flag on error
      isLogoutInProgressRef.current = false;
      
      throw error;
    }
  };

  useEffect(() => {
    logger.debug('🚀 AuthProvider mounted');
    isMountedRef.current = true;
    
    // Check if we've had an auth session previously
    try {
      hasHadAuthSession = sessionStorage.getItem('has_had_auth_session') === 'true';
    } catch (e) {
      // Ignore storage errors
    }

    const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {
      logger.debug('🔄 Auth state changed:', { 
        event, 
        hasSession: !!session,
        userId: session?.user?.id,
        userEmail: session?.user?.email,
        hasHadAuthSession
      });
      
      if (!isMountedRef.current) return;
      
      // MAJOR CHANGE: Lock all auth changes during MFA flow
      // Check if MFA is pending to prevent setting the user prematurely
      const isMfaVerificationPending = sessionStorage.getItem(MFA_PENDING_KEY) === 'true';
      const mfaVerificationCompleted = sessionStorage.getItem('mfa_verification_completed') === 'true';
      const mfaSignInInProgress = sessionStorage.getItem('mfa_signin_in_progress') === 'true';
      
      // If any part of the MFA flow is active, block auth state changes
      const isMfaFlowActive = isMfaVerificationPending || mfaSignInInProgress;
      
      // Track when MFA verification starts to implement timeouts if needed
      if (isMfaFlowActive && !mfaVerificationStartedAtRef.current) {
        mfaVerificationStartedAtRef.current = Date.now();
        logger.debug('🕒 Starting MFA verification timestamp tracking');
      }
      
      // If MFA was just completed, clear the completed flag and proceed with normal auth
      if (mfaVerificationCompleted) {
        logger.debug('🔓 MFA verification was just completed, proceeding with normal auth flow');
        try {
          sessionStorage.removeItem('mfa_verification_completed');
          mfaVerificationStartedAtRef.current = null; // Reset the timestamp
        } catch (e) {
          // Ignore storage errors
        }
      }
      // Otherwise, if MFA is still pending AND we have a valid factor ID, COMPLETELY BLOCK auth state updates
      else if (isMfaFlowActive && event !== 'SIGNED_OUT') {
        // Get the factor ID from session storage
        const factorId = sessionStorage.getItem(MFA_FACTOR_ID_KEY);
        const hasValidFactorId = !!factorId && factorId !== "factor_id must be an UUID";
        
        // Only block auth state updates if we have a valid factor ID
        if (hasValidFactorId) {
          logger.debug('🔒 BLOCKING auth state change during MFA flow', {
            event,
            hasMfaPending: isMfaVerificationPending,
            mfaSignInInProgress,
            hasValidFactorId,
            verificationStartedAt: mfaVerificationStartedAtRef.current
          });
          
          // Update MFA state but don't update user state
          // This is CRITICAL to prevent Home from flashing during MFA verification
          if (session) {
            setIsMfaPending(true);
            setCurrentMfaFactorId(factorId);
            const mfaEmailValue = sessionStorage.getItem(MFA_EMAIL_KEY);
            if (mfaEmailValue) {
              setMfaEmail(mfaEmailValue);
            }
          }
          return; // ⛔ EARLY RETURN - block all auth state updates during MFA verification
        } else {
          // No valid factor ID, so clear the MFA flags
          logger.warn('MFA flow active but no valid factor ID - clearing MFA flags');
          clearAllMfaFlags();
          // Continue with auth flow - don't return early
        }
      }
      
      // Add safety timeout for MFA flow - if MFA was started more than 2 minutes ago but never completed,
      // clear the flags to prevent users from getting stuck
      if (mfaVerificationStartedAtRef.current && 
          Date.now() - mfaVerificationStartedAtRef.current > 2 * 60 * 1000 &&
          session) {
        logger.warn('🕒 MFA verification timed out after 2 minutes - clearing flags to prevent users getting stuck');
        clearAllMfaFlags(); 
        // Continue with auth flow
      }
      
      // Special handling to prevent rapid auth state transitions
      if (event === 'SIGNED_OUT' || (event === 'INITIAL_SESSION' && !session)) {
        // If we recently had a user and this is initial load, be cautious about setting to null
        if (event === 'INITIAL_SESSION' && hasHadAuthSession) {
          logger.debug('🔍 Detected potential auth state inconsistency, double-checking session');
          
          // Don't immediately set to null, check if we can recover the session
          try {
            const { data } = await supabase.auth.getSession();
            if (data.session) {
              logger.debug('✅ Found valid session despite INITIAL_SESSION event with null session');
              return; // Don't update state, wait for proper session data
            }
          } catch (error) {
            // Proceed with logout if we can't recover session
            logger.error('❌ Failed to verify session during recovery attempt:', error);
          }
        }
        
        updateAuthState(null, false, false, false);
        
        // Don't show toast here - we'll handle this in the signOut function instead
        // This prevents duplicate toasts from the auth state change events
      } else if (session) {
        // Reset logout toast flag when user signs in
        logoutToastShownRef.current = false;
        
        // Set hasHadAuthSession when we get a session
        hasHadAuthSession = true;
        try {
          sessionStorage.setItem('has_had_auth_session', 'true');
        } catch (e) {
          // Ignore storage errors
        }
        
        if (event === 'SIGNED_IN' && 
            preloadedEmail === session.user.email && 
            preloadedOnboardingStatus !== null &&
            preloadSuccess) {
          // PERFORMANCE: Use preloaded data to avoid redundant database calls
          logger.debug('Using preloaded data for faster auth state update');
          updateAuthState(session.user, preloadedOnboardingStatus, false, false);
          
          // Temporarily disabled to avoid WebSocket issues
          // reinitializeSubscriptions().catch(error => {
          //   logger.error('Failed to reinitialize subscriptions after sign in:', error);
          // });
        } else {
          // PERFORMANCE: Only call checkUser if we don't have preloaded data
          // This reduces redundant database calls during login
          logger.debug('No preloaded data available, fetching user data');
          checkUser(true).catch(async (error) => {
            if (error.name !== 'AuthSessionMissingError') {
              await logError(error, 'AuthContext.authStateChange', { 
                event, 
                userId: session.user.id 
              });
            }
          });
          
          // Temporarily disabled to avoid WebSocket issues
          // if (event === 'SIGNED_IN') {
          //   reinitializeSubscriptions().catch(error => {
          //     logger.error('Failed to reinitialize subscriptions after sign in:', error);
          //   });
          // }
        }
      }
    });

    // CRITICAL CHANGE: Make initialization completely non-blocking
    // Set loading to false immediately and initialized to true
    // This allows the app to render immediately without waiting for any auth checks
    setLoading(false);
    setInitialized(true);
    
    // REMOVED: The initial checkUser call that was causing delays
    // The auth state will be determined by the onAuthStateChange listener above
    // which will fire immediately with the current session state
    
    logger.debug('🚀 AuthProvider initialization complete - app can render immediately');

    return () => {
      logger.debug('👋 AuthProvider unmounting');
      isMountedRef.current = false;
      if (authStateTimeoutRef.current) {
        clearTimeout(authStateTimeoutRef.current);
      }
      authListener.subscription.unsubscribe();
    };
  }, [checkUser]);

  // Update user identification for analytics when user changes
  useEffect(() => {
    if (user) {
      logger.debug('User authenticated for analytics', { userId: user.id });
    } else {
      logger.debug('User cleared from analytics');
    }
  }, [user]);

  // Sign in with MFA support
  const signInWithMfa = async (email: string, password: string): Promise<SignInResult> => {
    logger.debug('Starting signin with potential MFA verification');
    try {
      // Clear any stale MFA flags
      try {
        sessionStorage.removeItem(MFA_PENDING_KEY);
        sessionStorage.removeItem(MFA_FACTOR_ID_KEY);
        sessionStorage.removeItem(MFA_EMAIL_KEY);
        sessionStorage.removeItem('mfa_signin_in_progress');
        sessionStorage.removeItem('mfa_verification_completed');
        window.__MFA_SIGNIN_STARTED = false;
      } catch (e) {
        logger.warn('Unable to clear MFA flags in session storage:', e);
      }
      
      // Regular login
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      
      logger.debug('Sign in response:', {
        success: !!data.session,
        hasUser: !!data.user,
        hasError: !!error
      });
      
      if (error) {
        throw error;
      }
      
      if (data.session) {
        // Check if MFA is required - add retry logic for session establishment
        let mfaData = null;
        let mfaError = null;
        let retryCount = 0;
        const maxRetries = 3;
        
        while (retryCount < maxRetries && !mfaData) {
          try {
            // Small delay to allow session to be fully established
            if (retryCount > 0) {
              await new Promise(resolve => setTimeout(resolve, 500 * retryCount));
            }
            
            const result = await supabase.auth.mfa.listFactors();
            mfaData = result.data;
            mfaError = result.error;
            
            if (mfaError) {
              logger.warn(`MFA listFactors attempt ${retryCount + 1} failed:`, mfaError);
              retryCount++;
            } else {
              break;
            }
          } catch (error) {
            logger.warn(`MFA listFactors attempt ${retryCount + 1} threw error:`, error);
            mfaError = error;
            retryCount++;
          }
        }
        
        if (mfaError && retryCount === maxRetries) {
          logger.error('Failed to check MFA status after retries:', mfaError);
          // Continue with regular sign-in flow - MFA check failed
          return { success: true, requiresMfa: false };
        }
        
        const totpFactors = mfaData?.totp?.filter(factor => factor.status === 'verified') || [];
        
        if (totpFactors && totpFactors.length > 0) {
          // User has MFA enabled
          const factorId = totpFactors[0].id;
          logger.debug('MFA required, setting up verification', { 
            factorId: factorId.substring(0, 8) + '...',
            factorsCount: totpFactors.length
          });
          
          // Set MFA state
          try {
            sessionStorage.setItem(MFA_PENDING_KEY, 'true');
            sessionStorage.setItem(MFA_FACTOR_ID_KEY, factorId);
            sessionStorage.setItem(MFA_EMAIL_KEY, email);
            sessionStorage.setItem('mfa_signin_in_progress', 'true');
          } catch (e) {
            logger.warn('Unable to set MFA verification status in session storage:', e);
          }
          
          setIsMfaPending(true);
          setCurrentMfaFactorId(factorId);
          setMfaEmail(email);
          
          return { success: true, requiresMfa: true };
        }
        
        // No MFA required - proceed with normal login
        logger.debug('Login successful without MFA verification');
        
        // Clear any MFA flags
        clearAllMfaFlags();
        
        // PERFORMANCE: Initialize user data in background without blocking login
        if (data.session?.user) {
          // Don't await - let this run in background
          initializeUserDataAfterLogin(data.session.user.id).catch(error => {
            logger.warn('Background user data initialization failed:', error);
          });
        }
        
        return { success: true, requiresMfa: false };
      }
      
      return { success: false, requiresMfa: false, error: new Error('Login failed') };
    } catch (err) {
      // Clear MFA flags on error
      clearAllMfaFlags();
      
      const error = err instanceof Error ? err : new Error(String(err));
      logger.error('SignInWithMfa error:', error);
      await logError(error, 'AuthContext.signInWithMfa', { email });
      return { success: false, requiresMfa: false, error };
    }
  };
  
  // Complete MFA verification
  const completeMfaVerification = async (code: string) => {
    try {
      if (!currentMfaFactorId) {
        // Try to recover from session storage
        const storedFactorId = sessionStorage.getItem(MFA_FACTOR_ID_KEY);
        if (storedFactorId && storedFactorId !== "factor_id must be an UUID") {
          logger.warn('Recovered factorId from session storage in completeMfaVerification', { 
            recoveredId: storedFactorId.substring(0, 8) + '...' 
          });
          // Update the React state with the recovered ID
          setCurrentMfaFactorId(storedFactorId);
          
          // Proceed with the verification using the recovered ID
          return await completeMfaVerificationWithId(storedFactorId, code);
        }
        
        return { 
          success: false, 
          error: new Error('No MFA verification in progress') 
        };
      }
      
      return await completeMfaVerificationWithId(currentMfaFactorId, code);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      logger.error('Error in completeMfaVerification:', error);
      return { success: false, error };
    }
  };
  
  // Helper function to perform the actual verification
  const completeMfaVerificationWithId = async (factorId: string, code: string): Promise<{ success: boolean, error?: Error }> => {
    safeLog(logger, 'info', 'Completing MFA verification with factor ID and challenge', {
      factorId: factorId.substring(0, 8) + '...',
      codeLength: code ? code.length : 0
    });
    
    try {
      // NOTE: Supabase's typing is incorrect, we need to manually specify the parameters
      // Use challengeAndVerify which is the correct method for TOTP verification
      const { data, error } = await supabase.auth.mfa.challengeAndVerify({
        factorId,
        challengeType: 'totp',
        code
      } as any); // Use 'as any' to bypass typescript error until Supabase types are fixed
      
      if (error) {
        logger.error('Error verifying MFA code:', error);
        return { success: false, error: error instanceof Error ? error : new Error(String(error)) };
      }
      
      if (data) {
        logger.debug('MFA verification successful - performing complete cleanup');
        
        // Use our thorough cleanup function to ensure all MFA-related flags are removed
        clearAllMfaFlags();
        
        // CRITICAL: Also clear auth flow flags that might cause loading screens
        sessionStorage.removeItem('auth_flow_in_progress');
        sessionStorage.removeItem('auth_flow_email');
        sessionStorage.removeItem('auth_flow_timestamp');
        
        logger.debug('MFA verification successful - all flags cleared');
        return { success: true };
      }
      
      // If verification was successful but no data was returned, log and return
      logger.warn('MFA verification successful but no data was returned');
      clearAllMfaFlags(); // Still perform cleanup
      
      // Also clear auth flow flags
      sessionStorage.removeItem('auth_flow_in_progress');
      sessionStorage.removeItem('auth_flow_email');
      sessionStorage.removeItem('auth_flow_timestamp');
      
      return { success: true };
    } catch (parseError) {
      // Check if this might be a JSON parsing error from HTML response
      if (parseError instanceof SyntaxError && parseError.message.includes('Unexpected token')) {
        logger.error('Received HTML instead of JSON during MFA verification:', parseError);
        return { 
          success: false, 
          error: new Error('Authentication service returned an invalid response. Please try again later.') 
        };
      }
      
      // For other errors, log and return
      logger.error('Error during MFA verification:', parseError);
      return { 
        success: false, 
        error: parseError instanceof Error ? parseError : new Error('Failed to verify MFA code') 
      };
    }
  };
  
  // Cancel MFA verification process
  const cancelMfaVerification = () => {
    logger.debug('Canceling MFA verification process');
    
    // Use our thorough cleanup function
    clearAllMfaFlags();
    
    // Also sign out to clear any partial authentication
    supabase.auth.signOut().catch(error => {
      logger.error('Error signing out after MFA cancellation:', error);
    });
    
    logger.debug('MFA verification cancelled');
  };

  // Refresh MFA status when user changes
  useEffect(() => {
    if (user && !isMfaPending) {
      refreshMFAStatus();
    }
  }, [user, isMfaPending, refreshMFAStatus]);

  useEffect(() => {
    // When MFA verification becomes pending, set a timeout to auto-clean up
    // if the verification process takes too long (safety net)
    if (isMfaPending) {
      // Store the start time in session storage for tracking
      const startTime = Date.now();
      try {
        sessionStorage.setItem('mfa_verification_start_time', startTime.toString());
      } catch (e) {
        logger.warn('Unable to store MFA verification start time');
      }
      
      // Set a timeout to auto-clean up after 5 minutes
      const mfaTimeoutId = setTimeout(() => {
        const currentTime = Date.now();
        const startTimeStr = sessionStorage.getItem('mfa_verification_start_time');
        const parsedStartTime = startTimeStr ? parseInt(startTimeStr, 10) : startTime;
        const elapsedTime = currentTime - parsedStartTime;
        
        // Only auto-clean if MFA is still pending after 5 minutes
        if (isMfaPending && elapsedTime > 5 * 60 * 1000) {
          logger.warn('MFA verification timed out after 5 minutes - auto-cleaning up');
          
          // Clean up MFA state
          clearAllMfaFlags();
          setIsMfaPending(false);
          setCurrentMfaFactorId(null);
          setMfaEmail(null);
          
          // Remove route blocker
          try {
            const routeBlocker = document.getElementById('mfa-route-blocker');
            if (routeBlocker && routeBlocker.parentNode) {
              routeBlocker.parentNode.removeChild(routeBlocker);
              
              // Restore original history methods
              const restoreScript = document.createElement('script');
              restoreScript.id = 'mfa-restore-script';
              restoreScript.textContent = `
                if (window.originalPushState && window.originalReplaceState) {
                  history.pushState = window.originalPushState;
                  history.replaceState = window.originalReplaceState;
                  delete window.originalPushState;
                  delete window.originalReplaceState;
                  console.log('Restored original history methods due to MFA timeout');
                }
              `;
              document.head.appendChild(restoreScript);
            }
          } catch (e) {
            logger.error('Error removing route blocker during timeout:', e);
          }
        }
      }, 5 * 60 * 1000); // 5 minutes timeout
      
      return () => {
        clearTimeout(mfaTimeoutId);
      };
    }
  }, [isMfaPending]);

  // Add a new comprehensive user data initialization function
  const initializeUserDataAfterLogin = async (userId: string): Promise<void> => {
    safeLog(logger, 'info', 'Initializing user data after login', { userId: userId.substring(0, 8) + '...' });
    
    try {
      // PERFORMANCE: Run all data loading operations in parallel instead of sequentially
      const [onboardingStatus, userData] = await Promise.allSettled([
        // 1. Load onboarding status
        checkOnboardingStatusFromDB(userId),
        // 2. Fetch full user record to ensure profile data is loaded
        getUserRecord(userId)
      ]);

      // Process onboarding status result
      if (onboardingStatus.status === 'fulfilled' && onboardingStatus.value !== null) {
        setOnboardingComplete(onboardingStatus.value);
        safeLog(logger, 'debug', 'Loaded onboarding status', { onboardingComplete: onboardingStatus.value });
      } else if (onboardingStatus.status === 'rejected') {
        safeLog(logger, 'warn', 'Failed to load onboarding status', { error: onboardingStatus.reason });
      }

      // Process user data result
      if (userData.status === 'fulfilled' && !userData.value.error && userData.value.data) {
        safeLog(logger, 'debug', 'Loaded user record successfully', { 
          hasEmail: !!userData.value.data.email,
          hasProfile: !!userData.value.data
        });
      } else if (userData.status === 'rejected') {
        safeLog(logger, 'warn', 'Failed to load user record', { error: userData.reason });
      } else if (userData.status === 'fulfilled' && userData.value.error) {
        safeLog(logger, 'warn', 'User record query returned error', { error: userData.value.error });
      }
      
      // 3. Refresh MFA status (can run independently)
      refreshMFAStatus().catch(error => {
        safeLog(logger, 'warn', 'Failed to refresh MFA status', { error: error instanceof Error ? error.message : String(error) });
      });
      
      safeLog(logger, 'info', 'User data initialization complete', { userId: userId.substring(0, 8) + '...' });
    } catch (error) {
      safeLog(logger, 'error', 'Error initializing user data', {
        error: error instanceof Error ? error.message : String(error)
      });
      // Don't throw - we want to continue even if there are data loading errors
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user,
      userProfile,
      isAdmin, 
      loading, 
      onboardingComplete,
      onboardingLoading,
      signOut,
      updateOnboardingStatus,
      preloadUserStatus,
      refreshUserProfile,
      // MFA related values
      mfaEnabled,
      mfaLoading,
      mfaFactors,
      refreshMFAStatus,
      unenrollMFA,
      // MFA verification flow
      isMfaPending,
      currentMfaFactorId,
      mfaEmail,
      signInWithMfa,
      completeMfaVerification,
      cancelMfaVerification
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 